import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import NewsCard from "@/components/base/NewsCard";
import Tag from "@/components/base/Tag";
import ShareButton from "@/components/feature/ShareButton";
import { getNewsById, getRelatedNews, getMediaById } from "@/lib/catalog";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getNewsById(id) : undefined;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 麻豆网站`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", article.summary);
    }
  }, [article]);

  if (!article) {
    return (
      <div className="w-full px-4 md:px-6 py-24 flex flex-col items-center justify-center text-center">
        <i className="ri-file-unknow-line text-6xl text-foreground-300 mb-5" />
        <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2">资讯未找到</h1>
        <p className="text-sm text-foreground-500 mb-6">你访问的资讯可能已被删除或链接有误。</p>
        <Link
          to="/category/news"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = getRelatedNews(article, 3);
  const relatedMedia = article.relatedMediaId ? getMediaById(article.relatedMediaId) : undefined;
  const inlineImage = relatedMedia ? relatedMedia.backdrop : article.cover;

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <article className="lg:col-span-2 min-w-0">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/category/news" className="hover:text-primary-500 transition-colors">资讯</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800 line-clamp-1">{article.category}</span>
          </nav>

          <span className="inline-block px-2.5 py-1 rounded-md text-xs font-semibold bg-secondary-100 text-secondary-900 mb-3">
            {article.category}
          </span>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-snug mb-4">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-5 mb-6 border-b border-background-200">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-3-line" />
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {article.publishedAt}
            </span>
            <div className="ml-auto">
              <ShareButton title={article.title} text={article.summary} />
            </div>
          </div>

          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mb-6">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 影视资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <p className="text-base font-medium text-foreground-800 leading-relaxed mb-5 pl-4 border-l-4 border-accent-500">
            {article.summary}
          </p>

          <div className="space-y-5">
            {article.content.map((para, i) => (
              <div key={i}>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{para}</p>
                {i === 1 && (
                  <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mt-5">
                    <img
                      src={inlineImage}
                      alt={`${article.title}配图`}
                      title={`${article.title} 配图`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-8">
            {article.tags.map((tag) => (
              <Tag key={tag} label={`#${tag}`} tone="secondary" href="/category/news" />
            ))}
          </div>

          {relatedMedia && (
            <div className="mt-8 p-4 rounded-lg bg-primary-50 border border-primary-100 flex items-center gap-4">
              <div className="w-16 h-24 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                <img src={relatedMedia.cover} alt={relatedMedia.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-primary-600 mb-1">相关影视作品</p>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">{relatedMedia.title}</h3>
                <Link
                  to={`/detail/${relatedMedia.id}`}
                  className="text-xs font-medium text-primary-500 hover:text-primary-600 transition-colors"
                >
                  查看详情 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          )}
        </article>

        <aside className="lg:col-span-1">
          <h2 className="text-lg font-bold text-foreground-950 mb-4 pb-3 border-b-2 border-secondary-500 inline-block">
            相关文章
          </h2>
          <div className="flex flex-col gap-4">
            {related.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          <Link
            to="/category/news"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
          >
            浏览更多资讯
            <i className="ri-arrow-right-line" />
          </Link>
        </aside>
      </div>
    </div>
  );
}